import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    Megaphone,
    Calendar,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Heart,
    Sparkles,
    Bell
} from 'lucide-react';
import { getActiveAnnouncements, getAnnouncementImageUrl } from '../../services/announcement.service';

const DISMISSED_KEY = 'usiu_dismissed_announcements';

const categoryStyles = {
    'mental-health': {
        bg: 'bg-gradient-to-r from-purple-500 to-indigo-600',
        lightBg: 'bg-purple-50 dark:bg-purple-900/20',
        border: 'border-purple-200 dark:border-purple-800',
        text: 'text-purple-700 dark:text-purple-300',
        icon: Heart
    },
    'wellness': {
        bg: 'bg-gradient-to-r from-emerald-500 to-teal-600',
        lightBg: 'bg-emerald-50 dark:bg-emerald-900/20',
        border: 'border-emerald-200 dark:border-emerald-800',
        text: 'text-emerald-700 dark:text-emerald-300',
        icon: Sparkles
    },
    'awareness': {
        bg: 'bg-gradient-to-r from-rose-500 to-pink-600',
        lightBg: 'bg-rose-50 dark:bg-rose-900/20',
        border: 'border-rose-200 dark:border-rose-800',
        text: 'text-rose-700 dark:text-rose-300',
        icon: Megaphone
    },
    'workshop': {
        bg: 'bg-gradient-to-r from-blue-500 to-cyan-600',
        lightBg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        text: 'text-blue-700 dark:text-blue-300',
        icon: Calendar
    },
    'seminar': {
        bg: 'bg-gradient-to-r from-amber-500 to-orange-600',
        lightBg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        text: 'text-amber-700 dark:text-amber-300',
        icon: Calendar
    },
    'support-group': {
        bg: 'bg-gradient-to-r from-violet-500 to-purple-600',
        lightBg: 'bg-violet-50 dark:bg-violet-900/20',
        border: 'border-violet-200 dark:border-violet-800',
        text: 'text-violet-700 dark:text-violet-300',
        icon: Heart
    },
    'other': {
        bg: 'bg-gradient-to-r from-gray-500 to-slate-600',
        lightBg: 'bg-gray-50 dark:bg-gray-900/20',
        border: 'border-gray-200 dark:border-gray-800',
        text: 'text-gray-700 dark:text-gray-300',
        icon: Bell
    }
};

const DashboardEventBanner = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [dismissedIds, setDismissedIds] = useState([]);
    const [isPaused, setIsPaused] = useState(false);

    // Load dismissed announcements from localStorage
    useEffect(() => {
        try {
            const stored = localStorage.getItem(DISMISSED_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                // Clean up expired dismissals (older than 7 days)
                const now = Date.now();
                const validDismissals = parsed.filter(
                    (item) => now - item.dismissedAt < 7 * 24 * 60 * 60 * 1000
                );
                setDismissedIds(validDismissals.map((item) => item.id));

                // Update localStorage with cleaned data
                if (validDismissals.length !== parsed.length) {
                    localStorage.setItem(DISMISSED_KEY, JSON.stringify(validDismissals));
                }
            }
        } catch (error) {
            console.error('Error loading dismissed announcements:', error);
        }
    }, []);

    // Fetch announcements
    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const response = await getActiveAnnouncements();
                if (response.status === 'success' && response.data?.length > 0) {
                    // Filter out dismissed announcements
                    const visibleAnnouncements = response.data.filter(
                        (a) => !dismissedIds.includes(a._id)
                    );
                    setAnnouncements(visibleAnnouncements);
                }
            } catch (error) {
                console.error('Failed to fetch announcements:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAnnouncements();
    }, [dismissedIds]);

    // Auto-rotate announcements
    useEffect(() => {
        if (loading || announcements.length <= 1 || isPaused) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % announcements.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [announcements.length, isPaused, loading]);

    const dismissAnnouncement = (id) => {
        // Add to dismissed list
        const newDismissed = [...dismissedIds, id];
        setDismissedIds(newDismissed);

        // Save to localStorage with timestamp
        try {
            const stored = localStorage.getItem(DISMISSED_KEY);
            const existing = stored ? JSON.parse(stored) : [];
            existing.push({ id, dismissedAt: Date.now() });
            localStorage.setItem(DISMISSED_KEY, JSON.stringify(existing));
        } catch (error) {
            console.error('Error saving dismissed announcement:', error);
        }

        // Update visible announcements
        setAnnouncements((prev) => prev.filter((a) => a._id !== id));

        // Adjust current index if needed
        if (currentIndex >= announcements.length - 1) {
            setCurrentIndex(Math.max(0, announcements.length - 2));
        }
    };

    const dismissAll = () => {
        const allIds = announcements.map((a) => a._id);
        const newDismissed = [...dismissedIds, ...allIds];
        setDismissedIds(newDismissed);

        try {
            const stored = localStorage.getItem(DISMISSED_KEY);
            const existing = stored ? JSON.parse(stored) : [];
            const now = Date.now();
            allIds.forEach((id) => existing.push({ id, dismissedAt: now }));
            localStorage.setItem(DISMISSED_KEY, JSON.stringify(existing));
        } catch (error) {
            console.error('Error saving dismissed announcements:', error);
        }

        setAnnouncements([]);
    };

    const formatDateRange = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const options = { month: 'short', day: 'numeric' };

        if (start.toDateString() === end.toDateString()) {
            return start.toLocaleDateString('en-US', { ...options, year: 'numeric' });
        }

        return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
    };

    if (loading || announcements.length === 0) {
        return null;
    }

    const currentAnnouncement = announcements[currentIndex];
    const style = categoryStyles[currentAnnouncement.category] || categoryStyles.other;
    const CategoryIcon = style.icon;
    const imageUrl = getAnnouncementImageUrl(currentAnnouncement);

    return (
        <div
            className="mb-6 relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            <div className={`relative overflow-hidden rounded-2xl border ${style.border} ${style.lightBg} shadow-sm transition-all duration-300`}>
                {/* Dismiss all button (if multiple) */}
                {announcements.length > 1 && (
                    <button
                        onClick={dismissAll}
                        className="absolute top-2 right-12 z-20 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                    >
                        Dismiss all
                    </button>
                )}

                {/* Dismiss button */}
                <button
                    onClick={() => dismissAnnouncement(currentAnnouncement._id)}
                    className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors shadow-sm"
                    aria-label="Dismiss announcement"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4">
                    {/* Icon/Image */}
                    <div className="flex-shrink-0">
                        {imageUrl ? (
                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden shadow-md">
                                <img
                                    src={imageUrl}
                                    alt={currentAnnouncement.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        ) : (
                            <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl ${style.bg} flex items-center justify-center shadow-md transition-colors duration-300`}>
                                <CategoryIcon className="w-8 h-8 text-white" />
                            </div>
                        )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pr-8">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentAnnouncement._id}
                                initial={{ opacity: 0, x: 5 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -5 }}
                                transition={{ duration: 0.2 }}
                            >
                                {/* Category & Priority */}
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs font-semibold uppercase tracking-wider ${style.text}`}>
                                        {currentAnnouncement.category.replace('-', ' ')}
                                    </span>
                                    {currentAnnouncement.priority === 'high' && (
                                        <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] font-bold rounded uppercase">
                                            Important
                                        </span>
                                    )}
                                </div>

                                {/* Title */}
                                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1 truncate">
                                    {currentAnnouncement.title}
                                </h3>

                                {/* Description */}
                                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-1 mb-2">
                                    {currentAnnouncement.description}
                                </p>

                                {/* Date & Link */}
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                        <Calendar className="w-3.5 h-3.5" />
                                        {formatDateRange(currentAnnouncement.startDate, currentAnnouncement.endDate)}
                                    </span>

                                    {currentAnnouncement.link && (
                                        <a
                                            href={currentAnnouncement.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`inline-flex items-center gap-1 text-xs font-semibold ${style.text} hover:underline`}
                                        >
                                            Learn more
                                            <ExternalLink className="w-3 h-3" />
                                        </a>
                                    )}
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Indicators */}
                    {announcements.length > 1 && (
                        <div className="flex sm:flex-col gap-1.5 self-center">
                            {announcements.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentIndex(idx)}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentIndex
                                            ? `bg-gray-800 dark:bg-white scale-125`
                                            : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                                        }`}
                                    aria-label={`Go to announcement ${idx + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardEventBanner;
